import React, { Suspense, useEffect, useState, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import LazyMountAfterIdle from './components/LazyMountAfterIdle';
import RouteSEO from './components/RouteSEO';
import { captureAttributionFromUrl } from './lib/attribution';
import { trackEvent } from './lib/tracking';
import { useAnalytics } from './lib/analytics';
import { startGooglePhoneSync } from './lib/googlePhone';
import './styles/FormLegal.css';

/* Header + Footer are lazy-loaded so that (a) App.css (imported inside
   Header.jsx) is code-split into its own CSS chunk instead of shipping
   inside main.css, and (b) /lp/* landing pages never download either
   file. This cuts ~16 KiB of unused CSS off the LP's initial payload. */
const Header = lazy(() => import('./components/Header'));
const Footer = lazy(() => import('./components/Footer'));

/* Home is the largest page (~12 KiB unused when the user lands on any non-`/`
   route). Lazy-loaded like the other routes so it only ships when needed. */
const Home = lazy(() => import('./pages/Home'));

/* ChatWidget is heavy (Claude bot + Calendly + emoji parser) and NOT part of the
   critical render path. Lazy-load AND delay-mount via LazyMountAfterIdle so it
   never contributes to Total Blocking Time on initial paint. */
const ChatWidget = lazy(() => import('./components/ChatWidget'));

const StickyMobileCTA = lazy(() => import('./components/StickyMobileCTA'));

/* All non-landing routes are lazy-loaded.
   This dramatically reduces the initial JS bundle, which improves
   First Contentful Paint and Largest Contentful Paint — both Core Web Vitals
   that Google ranks for. Home stays eagerly loaded because it's the LCP root. */
const CarDetailing = lazy(() => import('./pages/CarDetailing'));
/* PPF page routes now use LandingPaintProtectionFilm (see below). */
const AboutCeramicCoating = lazy(() => import('./pages/AboutCeramicCoating'));
const ProtectionPackages = lazy(() => import('./pages/ProtectionPackages'));
const WindowTint = lazy(() => import('./pages/WindowTint'));
const Contact = lazy(() => import('./pages/Contact'));
const Executive = lazy(() => import('./pages/Executive'));
const Blogs = lazy(() => import('./pages/Blogs'));
const Shop = lazy(() => import('./pages/Shop'));
const BlogServicesBoca = lazy(() => import('./pages/BlogServicesBoca'));
const BlogFAQs = lazy(() => import('./pages/BlogFAQs'));
const BlogCeramicCoating = lazy(() => import('./pages/BlogCeramicCoating'));
const BlogCarDetailingBoca = lazy(() => import('./pages/BlogCarDetailingBoca'));
const BlogPPFBoca = lazy(() => import('./pages/BlogPPFBoca'));
const BlogWindowTinting = lazy(() => import('./pages/BlogWindowTinting'));
const BlogWindowTintBrands = lazy(() => import('./pages/BlogWindowTintBrands'));
const BlogCeramicCoatingWhy = lazy(() => import('./pages/BlogCeramicCoatingWhy'));
const BlogCeramicCoatingTeam = lazy(() => import('./pages/BlogCeramicCoatingTeam'));
const BlogPPFvsCeramic = lazy(() => import('./pages/BlogPPFvsCeramic'));
const BlogTop5Reasons = lazy(() => import('./pages/BlogTop5Reasons'));
const BlogCeramicCoatingCost = lazy(() => import('./pages/BlogCeramicCoatingCost'));
const BlogProtectCar = lazy(() => import('./pages/BlogProtectCar'));
const BlogDetailingCost = lazy(() => import('./pages/BlogDetailingCost'));
const AdminChats = lazy(() => import('./pages/AdminChats'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const MarineDetailing = lazy(() => import('./pages/MarineDetailing'));
const PlaneDetailing = lazy(() => import('./pages/PlaneDetailing'));
const CarWrapping = lazy(() => import('./pages/CarWrapping'));
const CorporateFleet = lazy(() => import('./pages/CorporateFleet'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsAndConditions = lazy(() => import('./pages/TermsAndConditions'));
const BoyntonBeach = lazy(() => import('./pages/BoyntonBeach'));
const DeerfieldBeach = lazy(() => import('./pages/DeerfieldBeach'));
const BocaPoint = lazy(() => import('./pages/BocaPoint'));
const BocaRaton = lazy(() => import('./pages/BocaRaton'));
const PompanoBeach = lazy(() => import('./pages/PompanoBeach'));
const WestBoca = lazy(() => import('./pages/WestBoca'));
const CoconutCreek = lazy(() => import('./pages/CoconutCreek'));
const CoralSprings = lazy(() => import('./pages/CoralSprings'));
const MissionBay = lazy(() => import('./pages/MissionBay'));
const LighthousePoint = lazy(() => import('./pages/LighthousePoint'));
const DetailingCostRedirect = lazy(() => import('./pages/DetailingCostRedirect'));
const CustomerInquiryList = lazy(() => import('./pages/CustomerInquiryList'));
const ThankYou = lazy(() => import('./pages/ThankYou'));
const AboutUs = lazy(() => import('./pages/AboutUs'));
const Reviews = lazy(() => import('./pages/Reviews'));
const LandingCeramicCoating = lazy(() => import('./pages/LandingCeramicCoating'));
const LandingCeramicCoatingTest = lazy(() => import('./pages/LandingCeramicCoatingTest'));
const LandingPaintProtectionFilm = lazy(() => import('./pages/LandingPaintProtectionFilm'));
const BlogPPFCompleteGuide = lazy(() => import('./pages/BlogPPFCompleteGuide'));
const BlogKovalentCeramicGuide = lazy(() => import('./pages/BlogKovalentCeramicGuide'));
const BlogMembershipMobile = lazy(() => import('./pages/BlogMembershipMobile'));
const BlogPPFCostBoca = lazy(() => import('./pages/BlogPPFCostBoca'));
const BlogCeramicVsWax = lazy(() => import('./pages/BlogCeramicVsWax'));
const BlogTeslaEVCoating = lazy(() => import('./pages/BlogTeslaEVCoating'));
const LandingInstagram = lazy(() => import('./pages/LandingInstagram'));
const PostcardLanding = lazy(() => import('./pages/PostcardLanding'));
const PostcardTerms = lazy(() => import('./pages/PostcardTerms'));

// Preserve legacy GA4 SPA pageviews, without repeating the initial pageview
// already queued by index.html. Ads phone configuration is never reissued.
function ScrollToTop() {
  const { pathname, search } = useLocation();

  useEffect(() => startGooglePhoneSync(), []);

  useEffect(() => {
    captureAttributionFromUrl();
    window.scrollTo(0, 0);
    const page_path = pathname + (search || '');
    if (!window.__PRERENDER__ && typeof window.gtag === 'function' && window.__ssfGooglePage !== page_path) {
      window.__ssfGooglePage = page_path;
      window.gtag('config', 'G-VB59RBQ7W8', {
        page_path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [pathname, search]);

  // Fire our own admin-dashboard analytics beacon on every SPA route change
  useAnalytics();

  return null;
}

/* Minimal route fallback — visually quiet, prevents layout shift. */
const RouteFallback = () => (
  <div
    style={{
      minHeight: '60vh',
      background: '#000',
    }}
    aria-hidden="true"
  />
);

/* Landing routes render standalone (no Header / Footer) — used for
   paid-ad traffic where distraction-free conversion is critical. */
const ChromeGate = ({ children }) => {
  const { pathname } = useLocation();
  const isLanding = pathname.startsWith('/lp/');
  const isAdmin = pathname.startsWith('/admin/') || pathname === '/admin6767';
  if (isLanding || isAdmin) return null;
  return children;
};

/* <main> wrapper that reserves space for the promo banner only on pages
   that actually render the header (same rule as ChromeGate) and only while
   the banner is visible (not dismissed). */
const AppMain = ({ promoVisible, children }) => {
  const { pathname } = useLocation();
  const isLanding = pathname.startsWith('/lp/');
  const isAdmin = pathname.startsWith('/admin/') || pathname === '/admin6767';
  const hasChrome = !(isLanding || isAdmin);
  return <main className={hasChrome && promoVisible ? 'has-promo-banner' : ''}>{children}</main>;
};

function App() {
  const [promoDismissed, setPromoDismissed] = useState(
    () => typeof window !== 'undefined' && localStorage.getItem('ssf_promo_dismissed') === '1'
  );

  const dismissPromo = () => {
    localStorage.setItem('ssf_promo_dismissed', '1');
    setPromoDismissed(true);
  };

  useEffect(() => {
    /* Global tel: link tracker — fires a `phone_click` micro-conversion for
       every phone-number tap without needing to edit each Header/Footer/etc.
       file individually. Micro-conversions feed Google Ads Smart Bidding. */
    const onDocClick = (e) => {
      const a = e.target.closest && e.target.closest('a[href^="tel:"]');
      if (!a) return;
      trackEvent('phone_click', {
        phone_number: (a.getAttribute('href') || '').replace('tel:', ''),
        surface: window.location.pathname,
        link_text: (a.textContent || '').trim().slice(0, 60),
      });
    };
    document.addEventListener('click', onDocClick, { capture: true });
    return () => document.removeEventListener('click', onDocClick, { capture: true });
  }, []);

  return (
    <HelmetProvider>
      <div className="App">
        <BrowserRouter>
        <ScrollToTop />
        <RouteSEO />
        <ChromeGate>
          <Suspense fallback={null}>
            <Header showPromo={!promoDismissed} onDismissPromo={dismissPromo} />
          </Suspense>
        </ChromeGate>
        <AppMain promoVisible={!promoDismissed}>
          <Suspense fallback={<RouteFallback />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/car-detailing" element={<CarDetailing />} />
              <Route path="/ceramic-coating" element={<AboutCeramicCoating />} />
              <Route path="/protection-packages" element={<ProtectionPackages />} />
              <Route path="/about-ceramic-coating" element={<Navigate to="/ceramic-coating" replace />} />
              <Route path="/paint-protection-film" element={<LandingPaintProtectionFilm />} />
              <Route path="/ppf" element={<LandingPaintProtectionFilm />} />
              <Route path="/window-tint" element={<WindowTint />} />
              <Route path="/contact-us" element={<Contact />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/executive" element={<Executive />} />
              <Route path="/blogs" element={<Blogs />} />
              <Route path="/blog/services-boca-raton" element={<BlogServicesBoca />} />
              <Route path="/blog/faqs" element={<BlogFAQs />} />
              <Route path="/blog/ceramic-coating-boca-raton" element={<BlogCeramicCoating />} />
              <Route path="/blog/car-detailing-boca-raton" element={<BlogCarDetailingBoca />} />
              <Route path="/blog/ppf-boca-raton" element={<BlogPPFBoca />} />
              <Route path="/blog/window-tinting" element={<BlogWindowTinting />} />
              <Route path="/blog/window-tint-brands" element={<BlogWindowTintBrands />} />
              <Route path="/blog/ceramic-coating-why" element={<BlogCeramicCoatingWhy />} />
              <Route path="/blog/ceramic-coating-team" element={<BlogCeramicCoatingTeam />} />
              <Route path="/blog/ppf-vs-ceramic" element={<BlogPPFvsCeramic />} />
              <Route path="/blog/top-5-reasons" element={<BlogTop5Reasons />} />
              <Route path="/blog/ceramic-coating-cost" element={<BlogCeramicCoatingCost />} />
              <Route path="/blog/protect-car-florida" element={<BlogProtectCar />} />
              <Route path="/blog/detailing-cost-florida" element={<BlogDetailingCost />} />
              <Route path="/shop" element={<Shop />} />
              <Route path="/marine-detailing" element={<MarineDetailing />} />
              <Route path="/plane-detailing" element={<PlaneDetailing />} />
              <Route path="/car-wrapping" element={<CarWrapping />} />
              <Route path="/corporate-fleet" element={<CorporateFleet />} />
              <Route path="/privacy-policy" element={<PrivacyPolicy />} />
              <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
              <Route path="/auto-detailing-boynton-beach-fl" element={<BoyntonBeach />} />
              <Route path="/auto-detailing-deerfield-beach-fl" element={<DeerfieldBeach />} />
              <Route path="/auto-detailing-boca-point-fl" element={<BocaPoint />} />
              <Route path="/auto-detailing-boca-raton-fl" element={<BocaRaton />} />
              <Route path="/auto-detailing-pompano-beach-fl" element={<PompanoBeach />} />
              <Route path="/auto-detailing-west-boca-fl" element={<WestBoca />} />
              <Route path="/auto-detailing-coconut-creek-fl" element={<CoconutCreek />} />
              <Route path="/auto-detailing-coral-springs-fl" element={<CoralSprings />} />
              <Route path="/auto-detailing-mission-bay-fl" element={<MissionBay />} />
              <Route path="/auto-detailing-lighthouse-point-fl" element={<LighthousePoint />} />
              <Route path="/car-detailing/how-much-does-car-detailing-cost-in-florida" element={<DetailingCostRedirect />} />
              <Route path="/customerinquirylist1092837465" element={<CustomerInquiryList />} />
              <Route path="/thank-you" element={<ThankYou />} />
              <Route path="/about-us" element={<AboutUs />} />
              <Route path="/reviews" element={<Reviews />} />
              <Route path="/admin/chats" element={<AdminChats />} />
              <Route path="/admin6767" element={<AdminDashboard />} />
              <Route path="/lp/ceramic-coating" element={<LandingCeramicCoating />} />
              <Route path="/lp/ceramic-coating-2" element={<LandingCeramicCoatingTest />} />
              {/* Old /lp/paint-protection-film Google Ads URL now redirects
                  to the single canonical /paint-protection-film route. */}
              <Route path="/lp/paint-protection-film" element={<Navigate to="/paint-protection-film" replace />} />
              <Route path="/lp/ppf" element={<Navigate to="/paint-protection-film" replace />} />
              <Route path="/blog/paint-protection-film-complete-guide" element={<BlogPPFCompleteGuide />} />
              <Route path="/blog/kovalent-ceramic-coating-complete-guide" element={<BlogKovalentCeramicGuide />} />
              <Route path="/blog/mobile-detailing-membership-packages" element={<BlogMembershipMobile />} />
              <Route path="/blog/ppf-cost-boca-raton" element={<BlogPPFCostBoca />} />
              <Route path="/blog/ceramic-vs-wax" element={<BlogCeramicVsWax />} />
              <Route path="/blog/tesla-ev-ceramic-coating" element={<BlogTeslaEVCoating />} />
              <Route path="/instagram" element={<LandingInstagram />} />
              <Route path="/postcard" element={<PostcardLanding />} />
              <Route path="/postcard-terms" element={<PostcardTerms />} />
              <Route path="/link" element={<LandingInstagram />} />
              <Route path="/bio" element={<LandingInstagram />} />
              {/* Catch-all — every unmatched URL renders NotFound which
                  emits <meta name="prerender-status-code" content="404">
                  so the prerender returns real HTTP 404 to bots. */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
        </AppMain>
        <ChromeGate>
          <Suspense fallback={null}>
            <Footer />
          </Suspense>
        </ChromeGate>
        <ChromeGate>
          <LazyMountAfterIdle>
            <Suspense fallback={null}>
              <ChatWidget />
            </Suspense>
          </LazyMountAfterIdle>
        </ChromeGate>
        <ChromeGate>
          <Suspense fallback={null}>
            <StickyMobileCTA />
          </Suspense>
        </ChromeGate>
        <Toaster position="top-right" />
      </BrowserRouter>
      </div>
    </HelmetProvider>
  );
}

export default App;
